// Cigar Lounge data model and sample data

export interface LoungeAmenities {
  fullBar: boolean;
  beerWine: boolean;
  food: boolean;
  foodType?: string; // "Full menu", "Light snacks", "Food trucks nearby"
  lockers: boolean;
  privateRooms: boolean;
  outdoorArea: boolean;
  wifi: boolean;
  tvs: boolean;
  membersOnly: boolean;
  walkInFriendly: boolean;
  retailHumidor: boolean;
  cuttingService: boolean;
  byob: boolean;
}

export interface LoungeHours {
  day: string;
  open: string;
  close: string;
  closed?: boolean;
}

export interface LoungeSpecial {
  id: string;
  title: string;
  description: string;
  type: 'cigar' | 'drink' | 'event' | 'combo';
  validDays?: string[]; // ['Monday', 'Tuesday'] or empty for all days
  validUntil?: string; // ISO date
  discount?: string; // "20% off", "$5 off", "BOGO"
}

export interface LoungeEvent {
  id: string;
  title: string;
  description: string;
  date: string; // ISO date
  time: string;
  type: 'tasting' | 'brand_rep' | 'live_music' | 'pairing' | 'members_night' | 'other';
  brandSponsor?: string;
  rsvpRequired: boolean;
  rsvpLink?: string;
}

export interface Lounge {
  id: string;
  googlePlaceId?: string; // Original Google Place ID for fetching details
  name: string;
  slug: string;

  // Location
  address: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  location: { lat: number; lng: number };

  // Contact
  phone?: string;
  email?: string;
  website?: string;
  instagram?: string;
  facebook?: string;

  // Details
  description: string;
  shortDescription?: string;
  imageUrl?: string;
  photos?: string[];

  // Hours
  hours: LoungeHours[];

  // Features
  amenities: LoungeAmenities;

  // Specials & Events
  specials: LoungeSpecial[];
  events: LoungeEvent[];

  // Ratings
  rating?: number; // 1-5
  reviewCount?: number;

  // Business
  claimed: boolean; // Has owner claimed this listing?
  verified: boolean;
  featured: boolean;

  // Metadata
  source: 'manual' | 'google_places' | 'community' | 'owner';
  createdAt: string;
  updatedAt: string;
}

// Check-in and rewards system
export interface CheckIn {
  id: string;
  userId: string;
  loungeId: string;
  timestamp: string;
  note?: string;
  linkedNoteId?: string; // Link to a tasting note from this visit
}

export interface Badge {
  id: string;
  name: string;
  description: string;
  icon: string;
  requirement: string;
  category: 'visits' | 'exploration' | 'social' | 'timing' | 'special';
}

export interface UserBadge {
  id: string;
  userId: string;
  badgeId: string;
  earnedAt: string;
  loungeId?: string; // Where it was earned, if applicable
}

// Badge definitions
export const BADGES: Badge[] = [
  {
    id: 'first-checkin',
    name: 'First Light',
    description: 'Check in to your first lounge',
    icon: '🔥',
    requirement: '1 check-in',
    category: 'visits',
  },
  {
    id: 'regular',
    name: 'Regular',
    description: 'Check in to the same lounge 10 times',
    icon: '🏠',
    requirement: '10 check-ins at one lounge',
    category: 'visits',
  },
  {
    id: 'explorer',
    name: 'Lounge Explorer',
    description: 'Visit 5 different lounges',
    icon: '🧭',
    requirement: '5 unique lounges',
    category: 'exploration',
  },
  {
    id: 'night-owl',
    name: 'Night Owl',
    description: 'Check in after 10pm',
    icon: '🦉',
    requirement: 'Check-in after 10pm',
    category: 'timing',
  },
  {
    id: 'early-bird',
    name: 'Early Bird',
    description: 'Check in before noon',
    icon: '🌅',
    requirement: 'Check-in before 12pm',
    category: 'timing',
  },
  {
    id: 'weekend-warrior',
    name: 'Weekend Warrior',
    description: 'Check in on 4 consecutive weekends',
    icon: '⚔️',
    requirement: '4 weekend check-ins in a row',
    category: 'timing',
  },
  {
    id: 'social-smoker',
    name: 'Social Smoker',
    description: 'Attend 3 lounge events',
    icon: '🤝',
    requirement: '3 event check-ins',
    category: 'social',
  },
  {
    id: 'road-tripper',
    name: 'Road Tripper',
    description: 'Visit lounges in 3 different states',
    icon: '🚗',
    requirement: '3 states visited',
    category: 'exploration',
  },
  {
    id: 'connoisseur',
    name: 'Connoisseur',
    description: 'Log 25 tasting notes at lounges',
    icon: '🎩',
    requirement: '25 lounge tasting notes',
    category: 'visits',
  },
  {
    id: 'fresh-threads',
    name: 'Fresh Threads',
    description: 'Discovered 7CL - keeping clothes smoke-free',
    icon: '✨',
    requirement: 'Learn about 7CL products',
    category: 'special',
  },
];

// Sample lounges (seed data)
export const SAMPLE_LOUNGES: Lounge[] = [
  {
    id: 'summit-cigars-bath',
    name: 'Summit Cigars',
    slug: 'summit-cigars-bath-oh',
    address: '1924 N Cleveland Massillon Rd',
    city: 'Bath',
    state: 'OH',
    zip: '44333',
    country: 'USA',
    location: { lat: 41.1684, lng: -81.6412 },
    phone: '(330) 576-6900',
    website: 'https://summitcigarco.com',
    facebook: 'https://facebook.com/summitcigarcompany',
    description: 'Premier cigar lounge in Northeast Ohio offering an extensive humidor selection, comfortable lounge seating, and a welcoming atmosphere for cigar enthusiasts of all experience levels.',
    shortDescription: 'Premier cigar lounge in Northeast Ohio',
    hours: [
      { day: 'Monday', open: '10:00', close: '21:00' },
      { day: 'Tuesday', open: '10:00', close: '21:00' },
      { day: 'Wednesday', open: '10:00', close: '21:00' },
      { day: 'Thursday', open: '10:00', close: '21:00' },
      { day: 'Friday', open: '10:00', close: '22:00' },
      { day: 'Saturday', open: '10:00', close: '22:00' },
      { day: 'Sunday', open: '12:00', close: '18:00' },
    ],
    amenities: {
      fullBar: false,
      beerWine: true,
      food: true,
      foodType: 'Light snacks',
      lockers: true,
      privateRooms: false,
      outdoorArea: true,
      wifi: true,
      tvs: true,
      membersOnly: false,
      walkInFriendly: true,
      retailHumidor: true,
      cuttingService: true,
      byob: false,
    },
    specials: [
      {
        id: 'summit-happy-hour',
        title: 'Happy Hour',
        description: '15% off all house blends',
        type: 'cigar',
        validDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        discount: '15% off',
      },
    ],
    events: [],
    rating: 4.8,
    reviewCount: 127,
    claimed: false,
    verified: true,
    featured: true,
    source: 'manual',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  },
  {
    id: 'burn-by-rocky-patel-naples',
    name: 'Burn by Rocky Patel',
    slug: 'burn-rocky-patel-naples-fl',
    address: '10801 Corkscrew Rd',
    city: 'Estero',
    state: 'FL',
    zip: '33928',
    country: 'USA',
    location: { lat: 26.4317, lng: -81.7962 },
    phone: '(239) 949-2876',
    website: 'https://burnbyrockypatel.com',
    description: 'Upscale cigar lounge and bar by Rocky Patel featuring premium cigars, craft cocktails, and a sophisticated atmosphere. Full bar service with an extensive whiskey selection.',
    shortDescription: 'Upscale lounge by Rocky Patel with full bar',
    hours: [
      { day: 'Monday', open: '11:00', close: '00:00' },
      { day: 'Tuesday', open: '11:00', close: '00:00' },
      { day: 'Wednesday', open: '11:00', close: '00:00' },
      { day: 'Thursday', open: '11:00', close: '00:00' },
      { day: 'Friday', open: '11:00', close: '02:00' },
      { day: 'Saturday', open: '11:00', close: '02:00' },
      { day: 'Sunday', open: '11:00', close: '00:00' },
    ],
    amenities: {
      fullBar: true,
      beerWine: true,
      food: true,
      foodType: 'Full menu',
      lockers: true,
      privateRooms: true,
      outdoorArea: true,
      wifi: true,
      tvs: true,
      membersOnly: false,
      walkInFriendly: true,
      retailHumidor: true,
      cuttingService: true,
      byob: false,
    },
    specials: [
      {
        id: 'burn-whiskey-wed',
        title: 'Whiskey Wednesday',
        description: '$2 off all whiskey pours',
        type: 'drink',
        validDays: ['Wednesday'],
        discount: '$2 off',
      },
      {
        id: 'burn-rocky-deal',
        title: 'Rocky Patel Feature',
        description: 'Buy 3 Rocky Patel cigars, get 1 free',
        type: 'cigar',
        discount: 'Buy 3 Get 1',
      },
    ],
    events: [
      {
        id: 'burn-tasting-jan',
        title: 'Rocky Patel New Release Tasting',
        description: 'Sample the latest releases from Rocky Patel with brand ambassador',
        date: '2025-01-15',
        time: '19:00',
        type: 'tasting',
        brandSponsor: 'Rocky Patel',
        rsvpRequired: true,
      },
    ],
    rating: 4.6,
    reviewCount: 342,
    claimed: false,
    verified: true,
    featured: true,
    source: 'manual',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  },
  {
    id: 'vinces-cigars-naples',
    name: "Vince's Cigars",
    slug: 'vinces-cigars-naples-fl',
    address: '12980 Tamiami Trail N',
    city: 'Naples',
    state: 'FL',
    zip: '34110',
    country: 'USA',
    location: { lat: 26.2698, lng: -81.7953 },
    phone: '(239) 594-4550',
    description: 'Family-owned cigar shop and lounge serving Naples since 1995. Known for personalized service, extensive selection, and a friendly atmosphere where newcomers and aficionados feel at home.',
    shortDescription: 'Family-owned since 1995, friendly atmosphere',
    hours: [
      { day: 'Monday', open: '09:00', close: '20:00' },
      { day: 'Tuesday', open: '09:00', close: '20:00' },
      { day: 'Wednesday', open: '09:00', close: '20:00' },
      { day: 'Thursday', open: '09:00', close: '20:00' },
      { day: 'Friday', open: '09:00', close: '21:00' },
      { day: 'Saturday', open: '09:00', close: '21:00' },
      { day: 'Sunday', open: '10:00', close: '18:00' },
    ],
    amenities: {
      fullBar: false,
      beerWine: true,
      food: false,
      lockers: false,
      privateRooms: false,
      outdoorArea: true,
      wifi: true,
      tvs: true,
      membersOnly: false,
      walkInFriendly: true,
      retailHumidor: true,
      cuttingService: true,
      byob: true,
    },
    specials: [
      {
        id: 'vinces-monday',
        title: 'Monday Madness',
        description: '10% off entire purchase',
        type: 'combo',
        validDays: ['Monday'],
        discount: '10% off',
      },
    ],
    events: [],
    rating: 4.7,
    reviewCount: 89,
    claimed: false,
    verified: true,
    featured: false,
    source: 'manual',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  },
  // Cleveland Area Lounges
  {
    id: 'the-grotto-cleveland',
    name: 'The Grotto',
    slug: 'the-grotto-cleveland-oh',
    address: '824 W St Clair Ave',
    city: 'Cleveland',
    state: 'OH',
    zip: '44113',
    country: 'USA',
    location: { lat: 41.5005, lng: -81.6954 },
    phone: '(216) 623-0300',
    website: 'https://www.grottocle.com',
    description: 'Open-air cigar lounge at Barley House with a members club vibe, craft cocktails, and late hours. Upscale hangout atmosphere perfect for evening cigars in downtown Cleveland.',
    shortDescription: 'Upscale open-air cigar lounge downtown',
    hours: [
      { day: 'Monday', open: '16:00', close: '02:00' },
      { day: 'Tuesday', open: '16:00', close: '02:00' },
      { day: 'Wednesday', open: '16:00', close: '02:00' },
      { day: 'Thursday', open: '16:00', close: '02:00' },
      { day: 'Friday', open: '16:00', close: '02:30' },
      { day: 'Saturday', open: '16:00', close: '02:30' },
      { day: 'Sunday', open: '16:00', close: '00:00' },
    ],
    amenities: {
      fullBar: true,
      beerWine: true,
      food: true,
      foodType: 'Full menu from Barley House',
      lockers: false,
      privateRooms: false,
      outdoorArea: true,
      wifi: true,
      tvs: true,
      membersOnly: false,
      walkInFriendly: true,
      retailHumidor: true,
      cuttingService: true,
      byob: false,
    },
    specials: [],
    events: [],
    rating: 4.5,
    reviewCount: 89,
    claimed: false,
    verified: true,
    featured: true,
    source: 'manual',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  },
  {
    id: 'cleveland-cigar-social-lounge',
    name: 'Cleveland Cigar & Social Lounge',
    slug: 'cleveland-cigar-social-lounge-oh',
    address: '5765 Canal Rd',
    city: 'Cleveland',
    state: 'OH',
    zip: '44125',
    country: 'USA',
    location: { lat: 41.4298, lng: -81.6329 },
    phone: '(216) 359-1087',
    website: 'https://www.clevelandcigarandsociallounge.com',
    instagram: 'ccsl_exclusive',
    description: 'Premier membership-oriented cigar lounge focused on premium cigar selection and a comfortable, networking-friendly environment for professionals and aficionados.',
    shortDescription: 'Premier membership cigar lounge',
    hours: [
      { day: 'Monday', open: '12:00', close: '22:00' },
      { day: 'Tuesday', open: '12:00', close: '22:00' },
      { day: 'Wednesday', open: '12:00', close: '22:00' },
      { day: 'Thursday', open: '12:00', close: '22:00' },
      { day: 'Friday', open: '12:00', close: '00:00' },
      { day: 'Saturday', open: '12:00', close: '00:00' },
      { day: 'Sunday', open: '12:00', close: '20:00' },
    ],
    amenities: {
      fullBar: true,
      beerWine: true,
      food: true,
      foodType: 'Light snacks',
      lockers: true,
      privateRooms: true,
      outdoorArea: false,
      wifi: true,
      tvs: true,
      membersOnly: true,
      walkInFriendly: false,
      retailHumidor: true,
      cuttingService: true,
      byob: false,
    },
    specials: [],
    events: [],
    rating: 4.7,
    reviewCount: 156,
    claimed: false,
    verified: true,
    featured: true,
    source: 'manual',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  },
  {
    id: 'edwins-cigar-bar',
    name: 'EDWINS Cigar Bar',
    slug: 'edwins-cigar-bar-cleveland-heights-oh',
    address: '13101 Shaker Square',
    city: 'Cleveland Heights',
    state: 'OH',
    zip: '44120',
    country: 'USA',
    location: { lat: 41.4789, lng: -81.5932 },
    phone: '(216) 921-3333',
    website: 'https://edwinsrestaurant.org/cigar-bar/',
    description: 'Vintage-style cigar and whiskey lounge with curated cigar list and strong spirits program. Features structured events like football nights, rare-pour tastings, and the Ash & Ember Society membership.',
    shortDescription: 'Vintage cigar & whiskey lounge',
    hours: [
      { day: 'Monday', open: '17:00', close: '23:00' },
      { day: 'Tuesday', open: '17:00', close: '23:00' },
      { day: 'Wednesday', open: '17:00', close: '23:00' },
      { day: 'Thursday', open: '17:00', close: '00:00' },
      { day: 'Friday', open: '17:00', close: '01:00' },
      { day: 'Saturday', open: '17:00', close: '01:00' },
      { day: 'Sunday', open: '16:00', close: '22:00' },
    ],
    amenities: {
      fullBar: true,
      beerWine: true,
      food: true,
      foodType: 'Small plates',
      lockers: false,
      privateRooms: false,
      outdoorArea: false,
      wifi: true,
      tvs: true,
      membersOnly: false,
      walkInFriendly: true,
      retailHumidor: true,
      cuttingService: true,
      byob: false,
    },
    specials: [
      {
        id: 'edwins-football',
        title: 'Football Nights',
        description: 'Watch the game with drink specials',
        type: 'event',
        validDays: ['Sunday', 'Monday', 'Thursday'],
      },
    ],
    events: [],
    rating: 4.6,
    reviewCount: 203,
    claimed: false,
    verified: true,
    featured: false,
    source: 'manual',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  },
  {
    id: 'robusto-briar-lakewood',
    name: 'Robusto & Briar',
    slug: 'robusto-briar-lakewood-oh',
    address: '14435 Detroit Ave',
    city: 'Lakewood',
    state: 'OH',
    zip: '44107',
    country: 'USA',
    location: { lat: 41.4819, lng: -81.8001 },
    phone: '(216) 221-2575',
    website: 'https://robustobriar.com',
    description: 'Modern cigar shop and lounge with a solid humidor, pipe tobacco, and comfortable seating. Great for both stocking up and enjoying a relaxed smoke.',
    shortDescription: 'Modern cigar shop & lounge',
    hours: [
      { day: 'Monday', open: '10:00', close: '20:00' },
      { day: 'Tuesday', open: '10:00', close: '20:00' },
      { day: 'Wednesday', open: '10:00', close: '20:00' },
      { day: 'Thursday', open: '10:00', close: '20:00' },
      { day: 'Friday', open: '10:00', close: '21:00' },
      { day: 'Saturday', open: '10:00', close: '21:00' },
      { day: 'Sunday', open: '12:00', close: '18:00' },
    ],
    amenities: {
      fullBar: false,
      beerWine: false,
      food: false,
      lockers: false,
      privateRooms: false,
      outdoorArea: false,
      wifi: true,
      tvs: true,
      membersOnly: false,
      walkInFriendly: true,
      retailHumidor: true,
      cuttingService: true,
      byob: true,
    },
    specials: [],
    events: [],
    rating: 4.8,
    reviewCount: 178,
    claimed: false,
    verified: true,
    featured: false,
    source: 'manual',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  },
  {
    id: 'jovanns-tobacco-shop',
    name: "Jovann's Tobacco Shop",
    slug: 'jovanns-tobacco-mayfield-heights-oh',
    address: '1429 SOM Center Rd',
    city: 'Mayfield Heights',
    state: 'OH',
    zip: '44124',
    country: 'USA',
    location: { lat: 41.5192, lng: -81.4582 },
    phone: '(440) 461-1667',
    website: 'https://www.clevelandcigar.net',
    description: 'Old-school tobacconist with one of the largest cigar selections in Ohio. Features a casual in-store lounge where you can hang out with a coffee and enjoy your purchase.',
    shortDescription: 'Classic tobacconist with huge selection',
    hours: [
      { day: 'Monday', open: '09:00', close: '20:00' },
      { day: 'Tuesday', open: '09:00', close: '20:00' },
      { day: 'Wednesday', open: '09:00', close: '20:00' },
      { day: 'Thursday', open: '09:00', close: '20:00' },
      { day: 'Friday', open: '09:00', close: '21:00' },
      { day: 'Saturday', open: '09:00', close: '21:00' },
      { day: 'Sunday', open: '10:00', close: '18:00' },
    ],
    amenities: {
      fullBar: false,
      beerWine: false,
      food: false,
      lockers: false,
      privateRooms: false,
      outdoorArea: false,
      wifi: true,
      tvs: false,
      membersOnly: false,
      walkInFriendly: true,
      retailHumidor: true,
      cuttingService: true,
      byob: true,
    },
    specials: [],
    events: [],
    rating: 4.9,
    reviewCount: 312,
    claimed: false,
    verified: true,
    featured: false,
    source: 'manual',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  },
];

// Helper functions
export const getDistanceFromLatLng = (
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number => {
  const R = 3959; // Earth's radius in miles
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

export const formatHours = (hours: LoungeHours[]): string => {
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long' });
  const todayHours = hours.find(h => h.day === today);
  if (!todayHours || todayHours.closed) return 'Closed today';
  return `${todayHours.open} - ${todayHours.close}`;
};

export const isOpenNow = (hours: LoungeHours[]): boolean => {
  const now = new Date();
  const today = now.toLocaleDateString('en-US', { weekday: 'long' });
  const todayHours = hours.find(h => h.day === today);
  if (!todayHours || todayHours.closed) return false;

  const currentTime = now.getHours() * 100 + now.getMinutes();
  const openTime = parseInt(todayHours.open.replace(':', ''));
  const closeTime = parseInt(todayHours.close.replace(':', ''));

  // Handle overnight hours (e.g., 11:00 - 02:00)
  if (closeTime < openTime) {
    return currentTime >= openTime || currentTime <= closeTime;
  }
  return currentTime >= openTime && currentTime <= closeTime;
};

// Amenity display helpers (only boolean amenities, not foodType which is a string)
export const AMENITY_ICONS: Partial<Record<keyof LoungeAmenities, { icon: string; label: string }>> = {
  fullBar: { icon: '🍸', label: 'Full Bar' },
  beerWine: { icon: '🍺', label: 'Beer & Wine' },
  food: { icon: '🍽️', label: 'Food Available' },
  lockers: { icon: '🔐', label: 'Cigar Lockers' },
  privateRooms: { icon: '🚪', label: 'Private Rooms' },
  outdoorArea: { icon: '☀️', label: 'Outdoor Seating' },
  wifi: { icon: '📶', label: 'Free WiFi' },
  tvs: { icon: '📺', label: 'TVs' },
  membersOnly: { icon: '🎫', label: 'Members Only' },
  walkInFriendly: { icon: '👋', label: 'Walk-ins Welcome' },
  retailHumidor: { icon: '🗄️', label: 'Retail Humidor' },
  cuttingService: { icon: '✂️', label: 'Cutting Service' },
  byob: { icon: '🍾', label: 'BYOB Allowed' },
};
